// packages/sdk/src/components/FeedbackButton.tsx

import React from 'react';
import { FeedbackModal } from './FeedbackModal';
import { useMetricShift } from './MetricShiftProvider';
import { submitFeedback } from '../utils/api';
import type {
  FieldConfig,
  AppearanceConfig,
  FeedbackPayload,
} from '../types';
import { GENERAL_PRESET } from '../types';

// --- Props ---

export interface FeedbackButtonProps {
  // Required
  projectId: string;

  // API
  apiKey?: string;
  apiUrl?: string;

  // Form
  fields?: FieldConfig[];
  widgetType?: 'nps' | 'csat' | 'general' | 'custom';

  // Appearance — mirrors AppearanceConfig for convenience
  appearance?: AppearanceConfig;

  // Callbacks
  onOpen?: () => void;
  onClose?: () => void;
  onSuccess?: (feedbackId: string) => void;
  onError?: (error: Error) => void;
}

// --- Helpers ---

const darkenHex = (hex: string, amount = 0.12): string => {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!match) return hex;
  const num = parseInt(match[1], 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)));
  const h = (v: number) => v.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
};

// --- Component ---

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  projectId,
  apiKey,
  apiUrl = 'https://metricshift-sdk-be-production.up.railway.app',
  fields = GENERAL_PRESET,
  widgetType = 'general',
  appearance = {},
  onOpen,
  onClose,
  onSuccess,
  onError,
}) => {
  // Pull open state + controls from provider
  // Falls back gracefully if used outside a provider
  let isOpen = false;
  let openWidget: (flowId?: string) => void = () => {};
  let closeWidget: () => void = () => {};

  try {
    const ctx = useMetricShift();
    isOpen = ctx.isOpen;
    openWidget = ctx.openWidget;
    closeWidget = ctx.closeWidget;
  } catch {
    // Used without provider — manage state locally
    const [localOpen, setLocalOpen] = React.useState(false);
    isOpen = localOpen;
    openWidget = () => setLocalOpen(true);
    closeWidget = () => setLocalOpen(false);
  }

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const {
    buttonText = 'Feedback',
    subtitleText,
    variant = 'outline',
    primaryColor = '#111827',
    position = 'bottom-right',
    avatarSrc,
    avatarAlt,
    avatarSize = 40,
  } = appearance;

  // --- Colour logic ---
  const solidBg = primaryColor;
  const solidHover = darkenHex(primaryColor);

  let bg = '#ffffff';
  let hoverBg = '#f3f4f6';
  let textColor = primaryColor;
  let borderColor = 'rgba(15,23,42,0.08)';

  if (variant === 'solid') {
    bg = solidBg;
    hoverBg = solidHover;
    textColor = '#ffffff';
    borderColor = 'transparent';
  } else if (variant === 'ghost') {
    bg = 'transparent';
    hoverBg = '#f3f4f6';
    borderColor = 'transparent';
  }

  // --- Position ---
  const positionStyles: React.CSSProperties =
    position === 'inline'
      ? { position: 'relative' }
      : {
          position: 'fixed',
          zIndex: 9999,
          ...(position.includes('bottom') ? { bottom: '20px' } : { top: '20px' }),
          ...(position.includes('right') ? { right: '20px' } : { left: '20px' }),
        };

  const shadow = position !== 'inline'
    ? '0 12px 30px rgba(15,23,42,0.12)'
    : 'none';

  // --- Handlers ---
  const handleOpen = () => {
    openWidget();
    onOpen?.();
  };

  const handleClose = () => {
    closeWidget();
    onClose?.();
  };

  const handleSubmit = async (payload: FeedbackPayload) => {
    try {
      const response = await submitFeedback(apiUrl, payload, apiKey);
      if (onSuccess && response?.id) onSuccess(response.id);
    } catch (err) {
      if (onError && err instanceof Error) onError(err);
      throw err;
    }
  };

  return (
    <>
      {/* Trigger button */}
      <div
        style={{
          ...positionStyles,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Optional avatar */}
        {avatarSrc && (
          <div
            aria-hidden={!avatarAlt}
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translate(-50%, -75%)',
              width: avatarSize,
              height: avatarSize,
              borderRadius: '9999px',
              overflow: 'hidden',
              boxShadow: '0 8px 22px rgba(15,23,42,0.22)',
              border: '2px solid #ffffff',
              backgroundColor: '#e5e7eb',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <img
              src={avatarSrc}
              alt={avatarAlt ?? ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}

        <button
          type="button"
          onClick={handleOpen}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          aria-label="Open feedback form"
          aria-pressed={isOpen}
          className="ms-feedback-button"
          style={{
            padding: avatarSrc ? '12px 18px' : '10px 18px',
            backgroundColor: isHovered ? hoverBg : bg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '9999px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: isFocused
              ? `${shadow}, 0 0 0 3px rgba(59,130,246,0.45)`
              : shadow,
            transition: 'all 0.2s ease',
            transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
            outline: 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              lineHeight: 1.2,
            }}
          >
            <span style={{ fontWeight: 600, fontSize: '15px' }}>
              {buttonText}
            </span>
            {subtitleText && (
              <span
                style={{
                  marginTop: '2px',
                  fontWeight: 400,
                  fontSize: '12px',
                  opacity: 0.9,
                }}
              >
                {subtitleText}
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Modal */}
      <FeedbackModal
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        projectId={projectId}
        fields={fields}
        appearance={appearance}
        widgetType={widgetType}
      />
    </>
  );
};